import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FormBase } from '../../../_classes/form-base.class';
import IResource from '../../../_interfaces/IResource';
import IRole from '../../../_interfaces/IRole';
import { EResources } from '../../../_resources/EResources';
import { ResourcesService } from '../../../_services/resources/resources.service';
import { RolesService } from '../../../_services/roles/roles.service';
import { SessionService } from '../../../_services/session/session.service';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { AdminFormActionsComponent } from '../../admin-ui/admin-form-actions/admin-form-actions.component';
import { FormsModule } from '@angular/forms';
import { ResourceLabelPipe } from '../../../_pipes/resource-label.pipe';

type PermissionDomainGroup = {
  code: string;
  resources: IResource[];
};

@Component({
  selector: 'app-form-role',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    InputTextModule,
    InputSwitchModule,
    AdminFormActionsComponent,
    ResourceLabelPipe,
  ],
  templateUrl: './form-role.component.html',
  styleUrl: './form-role.component.scss',
})
export class FormRoleComponent extends FormBase<IRole> implements OnInit {
  private readonly permissionDomainOrder = [
    'APPOINTMENTS',
    'CALENDAR_NOTES',
    'PATIENTS',
    'DOCUMENTS',
    'TREATMENTS',
    'SALES',
    'PAYMENTS',
    'TASKS',
    'STATISTICS',
    'ADMINISTRATION',
    'SETTINGS',
    'OTHER',
  ];

  @Input() showIntro = true;
  public submitted = false;
  public submitting = false;
  public resources: IResource[] = [];
  public groupedResources: PermissionDomainGroup[] = [];
  public permissionsStatus: Record<number, boolean> = {};

  public form: ReturnType<FormBuilder['group']>;

  constructor(
    public readonly session: SessionService,
    private readonly roleService: RolesService,
    private readonly resourcesService: ResourcesService,
    private readonly validatorService: ValidatorService,
    public readonly messageService: MessageService,
    public readonly translate: TranslateService,
    private readonly fb: FormBuilder,
  ) {
    super(session, messageService, translate);
    this.form = this.fb.group({
      name: ['', [Validators.required]],
    });
  }

  async execute(): Promise<void> {
    this.submitted = true;
    this.clearErrors();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.payload = {
      ...this.payload,
      name: this.form.controls['name'].value || '',
    };

    this.submitting = true;
    try {
      await super.execute();
    } finally {
      this.submitting = false;
    }
  }

  clear(): void {
    this.payload = {
      name: '',
    };

    Object.keys(this.permissionsStatus || {}).forEach((resourceId) => {
      this.permissionsStatus[+resourceId] = false;
    });

    this.form.reset({
      name: '',
    });
    this.submitted = false;
  }

  generatePayload(): IRole {
    return {
      name: this.payload.name,
    };
  }

  getResourceName(): string {
    return EResources.ROLES;
  }

  updateFn(payload: IRole): Observable<any> {
    return this.roleService.update(this.customers_id, this.id, payload);
  }

  createFn(payload: IRole): Observable<any> {
    return this.roleService.create(this.customers_id, payload);
  }

  properties(): string[] {
    return ['name'];
  }

  validate(): Promise<void> {
    return this.validatorService.role(this.payload);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.form.patchValue({
      name: this.payload?.name || '',
    });
    this.loadResources();
  }

  private getDomainOrder(domainCode: string): number {
    const idx = this.permissionDomainOrder.indexOf(domainCode);
    return idx >= 0 ? idx : this.permissionDomainOrder.length;
  }

  private normalizeDomainCode(resource: IResource): string {
    const explicitCode = `${resource?.domainCode || ''}`.trim().toUpperCase();
    if (explicitCode) {
      return explicitCode;
    }

    const fallback = `${resource?.name || ''}`
      .trim()
      .toUpperCase()
      .split('.')[0];
    return fallback || 'OTHER';
  }

  private humanizeDomainCode(domainCode: string): string {
    return `${domainCode || ''}`
      .split('_')
      .filter((part) => !!part)
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(' ');
  }

  private buildDomainGroups(resources: IResource[]): PermissionDomainGroup[] {
    const grouped = new Map<string, IResource[]>();

    for (const resource of resources) {
      const domainCode = this.normalizeDomainCode(resource);
      const list = grouped.get(domainCode) || [];
      list.push(resource);
      grouped.set(domainCode, list);
    }

    return Array.from(grouped.entries())
      .map(([code, domainResources]) => ({
        code,
        resources: domainResources,
      }))
      .sort((a, b) => {
        const domainDiff = this.getDomainOrder(a.code) - this.getDomainOrder(b.code);
        if (domainDiff !== 0) {
          return domainDiff;
        }

        return a.code.localeCompare(b.code);
      });
  }

  getDomainTitle(domainCode: string): string {
    const key = `ADMIN.ROLES.DETAIL.PERMISSION_DOMAINS.${domainCode}.TITLE`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }

    return this.humanizeDomainCode(domainCode);
  }

  getDomainDescription(domainCode: string): string {
    const key = `ADMIN.ROLES.DETAIL.PERMISSION_DOMAINS.${domainCode}.DESCRIPTION`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }

    return this.translate.instant(
      'ADMIN.ROLES.DETAIL.PERMISSION_DOMAINS.GENERIC_DESCRIPTION',
      {
        domain: this.humanizeDomainCode(domainCode),
      },
    );
  }

  get formTitleKey(): string {
    return this.mode === 'edit' ? 'COMMON.ROLE' : 'ADMIN.ROLES.ADD';
  }

  get formDescriptionKey(): string {
    return this.mode === 'edit'
      ? 'ADMIN.ROLES.DETAIL.EDIT_DESCRIPTION'
      : 'ADMIN.IA.ROLES.DESCRIPTION';
  }

  override create(payload: IRole): void {
    this.createFn(payload)
      .pipe(
        switchMap((createdRole) => {
          const roleId = this.resolveCreatedRoleId(createdRole);
          if (!roleId || this.mode !== 'create') {
            return of({ assignmentCount: 0 });
          }

          const selectedResourceIds = this.resources
            .map((resource) => resource.id)
            .filter((resourceId) => !!this.permissionsStatus[resourceId]);

          if (!selectedResourceIds.length) {
            return of({ assignmentCount: 0 });
          }

          return forkJoin(
            selectedResourceIds.map((resourceId) =>
              this.roleService
                .updatePermissions(
                  this.customers_id,
                  roleId,
                  resourceId,
                  'value',
                  true,
                )
                .pipe(
                  map(() => true),
                  catchError(() => of(false)),
                ),
            ),
          ).pipe(
            map((results) => ({
              assignmentCount: results.filter(Boolean).length,
            })),
          );
        }),
      )
      .subscribe({
        next: ({ assignmentCount }) => {
          this.clear();
          this.createMessageSuccess();
          if (assignmentCount > 0) {
            this.messageService.add({
              severity: 'info',
              summary: this.translate.instant('ADMIN.ROLES.CREATE.PERMISSIONS_SAVED_SUMMARY'),
              detail: this.translate.instant('ADMIN.ROLES.CREATE.PERMISSIONS_SAVED_DETAIL', {
                total: assignmentCount,
              }),
            });
          }
          this.onCreate();
        },
        error: () => {
          this.createMessageError();
        },
      });
  }

  private loadResources(): void {
    this.resourcesService.getAll(this.customers_id).subscribe({
      next: (resources) => {
        this.resources = (resources || [])
          .sort((a, b) => {
            const aOrder = Number.isFinite(Number(a?.sortOrder))
              ? Number(a.sortOrder)
              : Number.MAX_SAFE_INTEGER;
            const bOrder = Number.isFinite(Number(b?.sortOrder))
              ? Number(b.sortOrder)
              : Number.MAX_SAFE_INTEGER;

            if (aOrder !== bOrder) {
              return aOrder - bOrder;
            }

            return (a.name || '').localeCompare(b.name || '');
          });
        this.groupedResources = this.buildDomainGroups(this.resources);

        const assignedResources = new Set(
          (this.payload?.resources || [])
            .filter((resource: any) => !!resource?.value)
            .map((resource: any) => +resource.resources_id),
        );

        this.permissionsStatus = {};
        this.resources.forEach((resource) => {
          this.permissionsStatus[resource.id] = assignedResources.has(+resource.id);
        });
      },
      error: () => {
        this.resources = [];
        this.groupedResources = [];
        this.permissionsStatus = {};
      },
    });
  }

  private resolveCreatedRoleId(createdRole: unknown): number {
    if (!createdRole || typeof createdRole !== 'object') {
      return 0;
    }

    const id = Number((createdRole as any).id || (createdRole as any).role?.id || 0);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }
}
